import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Table } from '@getsynapse/design-system';
import { RequestsDrillDownItem } from 'types/store/normalized/insightsDrillDown';
import { SortingType } from 'utils/customTypes';
import { orderBy } from 'lodash';
import { REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS } from 'utils/constants/dashboard';
import RequestDrillDownTableHeader from './RequestDrillDownTableHeader/RequestDrillDownTableHeader';
import RequestDrillDownTableBody from './RequestDrillDownTableBody/RequestDrillDownTableBody';
import { useSelector } from 'react-redux';
import drilldownSelectors from 'state/normalized/insights/drilldownSelectors';
import DrillTableHeaderFilters from 'Pages/DashboardsDetailsPage/components/DrillTableHeaderFilters';
import { handleRequestExportData } from 'Pages/DashboardsDetailsPage/components/DrillDownSidePanel/drillDownDataExportHandlers';
import TableSkeletonLoader from 'Organisms/TableSkeletonLoader/TableSkeletonLoader';
import { insightDrilldownMetadataSelectors } from 'state/normalized/insightDrilldownMetadata/insightDrilldownMetadataSlice';
import { selectUserRole } from 'state/User/userSlice';
import { USER_ROLES } from 'utils/constants';

const columnMap = new Map<string, string>([
  [
    REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUEST_NUMBER,
    'request_number.value',
  ],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUEST_TITLE, 'title'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.STATUS, 'request_status'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TYPE, 'request_type'],
  [
    REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUESTER_NAME,
    'requester.first_name',
  ],
  [
    REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUEST_OWNER,
    'owners.0.first_name',
  ],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECTS, 'projects'],
  [
    REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.BUSINESS_UNIT,
    'business_team_title',
  ],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.INTERNAL_PRIORITY, 'ld_priority'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.FORM_USED, 'form_title'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CREATION_DATE, 'creation_date'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.SUBMISSION_DATE, 'submission_date'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DECISION_DATE, 'decision_date'],
  [REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.LAST_UPDATE, 'last_update_date'],
]);

const RequestDrillDownTable = () => {
  const items = useSelector(drilldownSelectors.selectActiveDrilldownRequests);
  const cardName = useSelector(
    drilldownSelectors.selectActiveDrilldownCardName
  );
  const userRole = useSelector(selectUserRole);
  const isLoading = useSelector(
    insightDrilldownMetadataSelectors.selectIsActiveDrilldownLoading
  );
  const exportItems = () => {
    handleRequestExportData(items);
  };
  const [orderByParam, setOrderByParam] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const orderRequestTableBy = (
    items: RequestsDrillDownItem[],
    column: string,
    order: SortingType
  ): RequestsDrillDownItem[] => {
    if (!items || items.length === 0) return [];

    const fieldName = columnMap.get(column) || '';
    const finalData = orderBy(items, [fieldName], [order]);
    return finalData;
  };

  const sortedItems = useMemo(() => {
    return orderRequestTableBy(items, orderByParam, order);
  }, [items, orderByParam, order]);

  const handleSort = useCallback(
    (column: string) => {
      const isSameColumn = column === orderByParam;
      const newOrder = isSameColumn && order === 'asc' ? 'desc' : 'asc';
      setOrderByParam(column);
      setOrder(newOrder);
    },
    [orderByParam, order]
  );

  return (
    <div>
      <DrillTableHeaderFilters
        title={cardName}
        exportEnabled
        onExportClick={exportItems}
      />

      <div
        className={classnames(
          'w-full rounded-b overflow-auto border border-neutral-lighter-two max-h-filtered-table-body'
        )}
      >
        {isLoading && <TableSkeletonLoader />}
        {!isLoading && (
          <Table
            className={classnames('relative', {
              'max-w-full border-0 w-full': items.length,
              'w-full': !items.length,
            })}
            canSelectRows={false}
            data={{
              headData: {
                stickyHeader: true,
                handleSort,
                headCells: [],
              },
              rows: [],
              total: items.length,
            }}
            data-cy='team-dashboards-table'
          >
            <RequestDrillDownTableHeader handleSort={handleSort} />
            <RequestDrillDownTableBody
              items={sortedItems}
              readOnly={userRole !== USER_ROLES.ADMIN}
            />
          </Table>
        )}
      </div>
    </div>
  );
};

export default RequestDrillDownTable;
